import {
  ALERT_MIN,
  ALERT_MAX,
  IMAGE_SLOTS
} from '@/utils/constants/rewardsDisplayConstants';
import {
  REWARD_TYPES,
  SINGLE_IMAGE_REWARD_TYPES
} from '@/utils/constants/rewardsConstants';
import {Constants} from './constants/commonConstants';
import {getCurrencySymbolWithName} from './commons';

// Check if reward type uses single image upload
export const isSingleImageRewardType = (rewardType) => {
  return rewardType ? SINGLE_IMAGE_REWARD_TYPES.includes(rewardType) : false;
};

// Extract image URLs from a product item (up to 3)
export const extractImageUrls = (productItem = {}) => {
  const candidates = [];
  const pushIfUrl = (v) => {
    if (typeof v === 'string' && /^https?:\/\//i.test(v)) candidates.push(v);
  };

  // Direct keys
  pushIfUrl(productItem.imageUrl);
  pushIfUrl(productItem.imageURL);
  pushIfUrl(productItem.defaultImageUrl);
  pushIfUrl(productItem.defaultImage);
  pushIfUrl(productItem.thumbnailUrl);
  pushIfUrl(productItem.image);

  // Nested shapes
  if (productItem.media && Array.isArray(productItem.media)) {
    productItem.media.forEach((m) => pushIfUrl(m?.cdnUrl || m?.url));
  }
  if (Array.isArray(productItem.images)) {
    productItem.images.forEach((im) => pushIfUrl(im?.cdnUrl || im?.url || im));
  }
  if (Array.isArray(productItem.imageUrls)) {
    productItem.imageUrls.forEach((u) => pushIfUrl(u));
  }

  return [...new Set(candidates)].slice(0, 3);
};

// Map extracted URLs to image slot format
export const mapUrlsToImageSlots = (urls) => [
  {position: 1, url: urls[0] || '', file: null},
  {position: 2, url: urls[1] || '', file: null},
  {position: 3, url: urls[2] || '', file: null}
];

// Build completion warning message for products
export const buildCompletionWarningMessage = (
  displayState,
  selected,
  languages,
  rewardType,
  rewardText
) => {
  const isBonusProducts = rewardType === REWARD_TYPES.BONUS_PRODUCTS;
  const status = selected.map((p) => {
    const id = p.value;
    const pd = displayState?.productDisplay?.[id];
    const {imageOk, textOk, alertOk} = getProductCompletion(
      rewardType,
      pd,
      languages,
      rewardText
    );
    return {label: p.label, imageOk, textOk, alertOk};
  });

  const anyMissingImage = status.some((s) => !s.imageOk);
  // For BONUS_PRODUCTS, alertDisplayCount is now at reward level, not per-product
  const anyMissingAlert = !isBonusProducts && status.some((s) => !s.alertOk);
  const anyMissingText = status.some((s) => !s.textOk);
  // For BONUS_PRODUCTS, don't check alertOk per-product
  const productsWithMissing = isBonusProducts
    ? status.filter((s) => !s.imageOk || !s.textOk)
    : status.filter((s) => !s.imageOk || !s.alertOk || !s.textOk);

  if (productsWithMissing.length === 0) return null;

  const parts = [];
  if (anyMissingImage) parts.push('Image(s)');
  if (anyMissingAlert) parts.push('Alert display count');
  if (anyMissingText) parts.push('Title and Description for all languages');

  const productList = productsWithMissing.map((s) => s.label).join(', ');
  return `${parts.join(', ')} must be specified for all selected products.\n${productList}`;
};

// Check if EN autofill should be disabled based on patch
export const shouldDisableEnAutofill = (language, patch) => {
  if (language !== 'en') return false;
  const titleCleared =
    Object.prototype.hasOwnProperty.call(patch, 'title') && patch.title === '';
  const descCleared =
    Object.prototype.hasOwnProperty.call(patch, 'description') &&
    patch.description === '';
  return titleCleared || descCleared;
};

// Get sanitized alert display count
export const getSanitizedAlertCount = (raw) => {
  let num = Number(raw);
  if (!Number.isFinite(num)) num = 0;
  return Math.max(ALERT_MIN, Math.min(ALERT_MAX, num));
};

// Determine initial title based on reward type and form data
export const getInitialTitle = (formData, displaySingleImageUpload) => {
  const rewardType = formData?.rewardType;
  const isPepVoucher = rewardType === REWARD_TYPES.PEP_VOUCHER_PRODUCT;
  const isProductType =
    rewardType === REWARD_TYPES.PRODUCT ||
    rewardType === REWARD_TYPES.BONUS_PRODUCTS;

  if (isPepVoucher) {
    return formData?.selectedPepVouchersProducts?.[0]?.label || '';
  }
  if (isProductType) {
    return formData?.selectedProducts?.[0]?.label || '';
  }
  if (displaySingleImageUpload && rewardType) {
    // Capitalize first letter of reward type
    return (
      rewardType.charAt(0).toUpperCase() + rewardType.slice(1).toLowerCase()
    );
  }
  return '';
};

// Create an empty images array of length IMAGE_SLOTS
export const createEmptyImages = (count = IMAGE_SLOTS) =>
  Array.from({length: count}, (_, i) => ({
    position: i + 1,
    url: '',
    file: null
  }));

// Clamp alert display count to allowed bounds
export const clampAlertDisplayCount = (
  val,
  min = ALERT_MIN,
  max = ALERT_MAX
) => {
  let num = Number(val);
  if (!Number.isFinite(num)) num = 0;
  return Math.max(min, Math.min(max, num));
};

// Compute completion status for a productDisplay entry
// For BONUS_PRODUCTS: Individual product completion = image + per-product rewardText (alertDisplayCount is now global, not per-product)
// For other types: Individual product completion = image + alert (rewardText is global)
export const getProductCompletion = (
  rewardType,
  pd = {},
  languages = [],
  globalRewardText = null
) => {
  const imgs = pd?.images || [];
  const imageOk =
    Array.isArray(imgs) &&
    imgs.some(
      (img) => typeof img?.url === 'string' && img.url.trim().length > 0
    );

  // For BONUS_PRODUCTS, use per-product rewardText; otherwise use global rewardText
  const isBonusProducts = rewardType === REWARD_TYPES.BONUS_PRODUCTS;
  const rewardText = isBonusProducts
    ? pd?.rewardText || {}
    : globalRewardText || {};
  const langs = Array.isArray(languages) ? languages : [];
  const textOk = langs.every((l) => {
    const code = l.code || l;
    const t = rewardText?.[code]?.title;
    const d = rewardText?.[code]?.description;
    const hasTitle = typeof t === 'string' && t.trim().length > 0;
    const hasDesc = typeof d === 'string' && d.trim().length > 0;
    return hasTitle && hasDesc;
  });

  // For BONUS_PRODUCTS, alertDisplayCount is now at reward level (formData.alertDisplayCount), not per-product
  // For other reward types, alertDisplayCount was never required per-product (only BONUS_PRODUCTS used it)
  // So alertOk is always true for per-product completion check
  const alertOk = true;

  const completed = isBonusProducts
    ? Boolean(imageOk && textOk)
    : Boolean(imageOk && alertOk);
  return {imageOk, textOk, alertOk, completed};
};

// Ensure defaults for a productDisplay entry, returns a merged object
export const ensureProductDisplayDefaults = (
  productId,
  display,
  effectiveProducts,
  currentPd = {},
  defaultDescription = ''
) => {
  const images =
    currentPd?.images && currentPd.images.length > 0
      ? currentPd.images
      : createEmptyImages();
  const rewardText = currentPd?.rewardText || {};

  const activeProduct = (effectiveProducts || []).find(
    (p) => p.value === productId
  );
  const defaultTitle = activeProduct?.label || '';

  // Build EN reward text defaults without forcing empty description
  const existingEn = rewardText?.en || {};
  const enDescription =
    existingEn.description !== undefined &&
    existingEn.description !== null &&
    existingEn.description !== ''
      ? existingEn.description
      : defaultDescription || existingEn.description;

  // Note: alertDisplayCount is now at reward level (formData.alertDisplayCount) for BONUS_PRODUCTS, not per-product
  // Note: showOnHomePage is now at reward level (formData.showOnHomePage) for all reward types, not per-product
  const ensured = {
    useDefaultImage: Boolean(currentPd?.useDefaultImage),
    images,
    defaultImages: currentPd?.defaultImages || [],
    rewardText: {
      ...rewardText,
      en: {
        ...existingEn,
        title: existingEn.title ?? defaultTitle,
        ...(enDescription !== undefined ? {description: enDescription} : {})
      }
    }
  };

  return ensured;
};

// Helper function to get product display text
export const getProductDisplayText = (formData) => {
  if (!formData) return 'selected product';

  // Handle PHYSICAL and LOYALTY reward types first
  if (formData.rewardType === REWARD_TYPES.PHYSICAL) {
    return `[${Constants.PhysicalProduct}]`;
  }

  if (formData.rewardType === REWARD_TYPES.LOYALTY) {
    return `[${Constants.LoyaltyItem}]`;
  }

  // Select appropriate products array based on reward type
  // PRODUCT type uses selectedProducts, PEP_VOUCHER_PRODUCT uses selectedPepVouchersProducts
  let products = formData.selectedProducts;
  if (formData.rewardType === REWARD_TYPES.PEP_VOUCHER_PRODUCT) {
    products = formData.selectedPepVouchersProducts;
  }

  if (!products?.length) return 'selected product';

  if (products.length === 1) {
    const product = products[0];
    return product.label || product.productName || 'selected product';
  }

  return `${products.length} selected products`;
};

// Helper function to render bonus products preview
const renderBonusProductsPreview = (formData) => {
  const orderQuantity = formData.orderQty ?? formData.orderQuantity;
  if (orderQuantity) {
    return `Get a bonus product after every ${orderQuantity} orders`;
  }
  return '';
};

// Helper function to render product/PEP voucher product/physical preview
const renderProductPreview = (formData, prefix = '') => {
  const points = formData.redeemPoints || formData.points;
  if (points) {
    return `Get ${prefix}${getProductDisplayText(formData)} when you redeem ${points} points`;
  }
  return '';
};

// Helper function to render cash preview
// CASH type uses value field in UI (mapped to rewardCashAmount in API)
const renderCashPreview = (formData, currencySymbol) => {
  const points = formData.redeemPoints || formData.points;
  const amount = formData.value;
  if (amount && points) {
    return `Get ${currencySymbol} ${amount} cash when you redeem ${points} points.`;
  }
  return '';
};

// Helper function to render discount preview (exported for reuse in Activity Pay by Points)
export const renderDiscountPreview = (formData, currencySymbol) => {
  const points = formData.redeemPoints || formData.points;
  if (formData.value && points) {
    return `Get ${currencySymbol} ${formData.value} Discount when you redeem ${points} points.`;
  }
  return '';
};

// Helper function to render PEP voucher discount preview
const renderPepVoucherDiscountPreview = (formData, currencySymbol) => {
  const points = formData.redeemPoints || formData.points;
  if (formData.value && points) {
    return `Get ${currencySymbol} ${formData.value} PepVoucher - Discount when you redeem ${points} points.`;
  }
  return '';
};

// Helper function to render visa card preview
const renderVisaCardPreview = (formData, currencySymbol) => {
  if (
    formData?.rewardCashAmount &&
    (formData?.redeemPoints || formData?.points)
  ) {
    return `Get ${currencySymbol} ${formData?.rewardCashAmount || '-'} Visa Card when you redeem ${formData?.redeemPoints || formData?.points || '-'} points`;
  }
  return '';
};

// Helper function to render points preview
const renderPointsPreview = (formData) => {
  const points = formData.redeemPoints || formData.points;
  if (points) {
    return `Get ${points} points as reward`;
  }
  return '';
};

// Helper function to render loyalty preview
const renderLoyaltyPreview = (formData) => {
  const points = formData.redeemPoints || formData.points;
  if (!points) return '';

  // [Loyalty Item] is literal text, not a placeholder
  return `Get [Loyalty Item] when you redeem ${points} points`;
};

// Helper function to render digital preview
const renderDigitalPreview = (formData, currencySymbol) => {
  const amount = formData.digitalCodeAmount ?? formData.digitalValue;
  const points = formData.redeemPoints;
  const supplier = formData.digitalCodePartner ?? formData.digitalSupplier;
  if (amount && points && supplier) {
    return `Get ${currencySymbol} ${amount} ${supplier} gift card when you redeem ${points} points.`;
  }
  return '';
};

// Helper function to render preview based on reward type
export const renderPreviewContent = ({formData, countryCode}) => {
  if (!formData) return '';

  const currencySymbol = countryCode
    ? getCurrencySymbolWithName(countryCode)
    : '$';

  switch (formData.rewardType) {
    case REWARD_TYPES.BONUS_PRODUCTS:
      return renderBonusProductsPreview(formData);

    case REWARD_TYPES.PRODUCT:
      return renderProductPreview(formData);

    case REWARD_TYPES.PEP_VOUCHER_PRODUCT:
      return renderProductPreview(formData, 'PepVoucher for ');

    case REWARD_TYPES.PHYSICAL:
      return renderProductPreview(formData);

    case REWARD_TYPES.CASH:
      return renderCashPreview(formData, currencySymbol);

    case REWARD_TYPES.DISCOUNT:
      return renderDiscountPreview(formData, currencySymbol);

    case REWARD_TYPES.PEP_VOUCHER_DISCOUNT:
      return renderPepVoucherDiscountPreview(formData, currencySymbol);

    case REWARD_TYPES.VISA_CARD:
      return renderVisaCardPreview(formData, currencySymbol);

    case REWARD_TYPES.POINTS:
      return renderPointsPreview(formData);

    case REWARD_TYPES.DIGITAL:
    case REWARD_TYPES.DIGITAL_PRE_ORDER: {
      return renderDigitalPreview(formData, currencySymbol);
    }

    case REWARD_TYPES.LOYALTY:
      return renderLoyaltyPreview(formData);

    default:
      return '';
  }
};

// Shift images left after deletion and normalize positions
export const shiftImagesAfterDelete = (images, deletedIndex) => {
  const kept = images.filter((_, i) => i !== deletedIndex);
  const shifted = kept.map((img, i) => ({...img, position: i + 1}));
  return [
    shifted[0] || {position: 1, url: '', file: null},
    shifted[1] || {position: 2, url: '', file: null},
    shifted[2] || {position: 3, url: '', file: null}
  ];
};

// Update image at position with new data
export const updateImageAtPosition = (images, position, data) => {
  return images.map((img) =>
    img.position === position ? {position, ...data} : img
  );
};
